<template>
  <div
    v-if="productIsLoaded"
    ref="ProductCanvas"
    class="ProductCanvas md:h-full xs:justify-center md:justify-end"
    @click="clickHandler"
  >
    <div
      v-if="displayWarning"
      class="absolute pt bg-white border-sh-warning border-2 p-2 px-10 py-4"
      style="z-index: 2500"
    >
      <base-icon
        name="exclamationmark-icon"
        size="sm"
        class="absolute pin-l pin-t text-sh-warning p-2"
      />
      <base-icon
        name="close-icon"
        size="xs"
        class="cursor-pointer absolute pin-r pin-t p-2"
        @click.native="closeSizeWarning"
      />
      <!-- eslint-disable vue/no-v-html -->
      <p v-html="warningMessage" />
      <!-- eslint-enable vue/no-v-html -->
    </div>

    <div v-if="showMobileNavigation" class="navigator">
      <base-icon
        class="p-2"
        name="arrow-left-icon"
        fill="#0082d5"
        @click.native="prev"
      ></base-icon>
      <base-icon
        class="p-2"
        name="arrow-right-icon"
        fill="#0082d5"
        @click.native="next"
      ></base-icon>
    </div>

    <editor-control-layer
      :content="activePrintPlate.content"
      :style="computedStyle"
      :ratio="ratio"
      :clip-path="clipPath"
      :show-grid="showGrid"
      class="absolute"
      @input="updatePrintplate($event, activePrintSide)"
    ></editor-control-layer>
    <VisualAidMenu
      id="visual-aid-menu"
      class="absolute z-10"
      :elements-to-hide="['header', 'h1.sh-category-headline']"
      @toggleGrid="toggleGrid"
    ></VisualAidMenu>
    <img
      ref="ProductImage"
      :src="imageSource"
      alt="Product Photo"
      class="ProductImage md:h-full"
      @load="computeCanvasSize"
      @mousedown="unselectActiveItem"
    />
    <product-side-switcher v-if="!isMobile" />
    <print-side-swiper v-else ref="swiper"></print-side-swiper>
    <div class="absolute print-style-label">
      {{ $t('customization.activePrintStyle') }}:
      {{ $t(`printStyles.${getPrintStyle}.name`) }}
    </div>
  </div>
  <div v-else>Loading product...</div>
</template>
<script>
import ProductSideSwitcher from './ProductSideSwitcher.vue';
import isMobile from '../../utils/is-mobile';
import { MUTATION_COMPOSITION_UPDATE_SIDE_CONTENT } from '@/store/modules/composition/mutation-types';
import { MUTATION_SET_PRINT_SIDE } from '@/store/mutation-types';
import { GETTER_PRODUCT_PRINT_AREA } from '@/store/modules/product/getters';
import {
  GETTER_ACTIVE_PRINT_SIDE,
  GETTER_ACTIVE_PRINT_SIDE_PRINT_STYLE
} from '@/store/getters';
import PrintSideSwiper from './PrintSideSwiper.vue';
import EditorControlLayer from '@/components/control-layer/EditorControlLayer';
import { mapActions, mapState } from 'pinia/dist/pinia';
import { useControlLayerStore } from '@/store-pinia/control-layer';
import Redirect from '@/mixins/redirect';
import { useCompositionWarningStore } from '@/store-pinia/composition-warnings';

import { DEFAULT_RECTANGLE_CLIP_PATH } from '@/plugins/composition-renderer/constants';
import VisualAidMenu from './VisualAidMenu.vue';

export default {
  name: 'ProductCanvas',
  components: {
    EditorControlLayer,
    ProductSideSwitcher,
    PrintSideSwiper,
    VisualAidMenu
  },
  mixins: [Redirect],
  data: () => ({
    productImageHasLoaded: false,
    initialWidth: 0,
    initialHeight: 0,
    scaleX: 1,
    scaleY: 1,
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    ratio: 1,
    clipPath: DEFAULT_RECTANGLE_CLIP_PATH,
    isMobile: isMobile(),
    showGrid: false
  }),
  computed: {
    showMobileNavigation() {
      return (
        this.isMobile && Object.keys(this.composition.printPlates).length > 1
      );
    },
    computedStyle() {
      return {
        top: `${this.top}px`,
        left: `${this.left}px`,
        width: `${this.width}px`,
        height: `${this.height}px`
      };
    },
    imageSource() {
      return this.$store.getters['product/image'](this.activePrintSide);
    },
    productIsLoaded() {
      return this.$store.state.product.isLoaded;
    },
    activePrintPlate() {
      return this.composition.printPlates[this.activePrintSide];
    },
    composition() {
      return this.$store.state.composition.composition;
    },
    activePrintSide() {
      return this.$store.state.activePrintSide;
    },
    ...mapState(useControlLayerStore, ['activeElement']),
    displayWarning() {
      if (null === this.activeElement) {
        return false;
      }
      return this.hasActiveWarnings(this.activeElement.uid);
    },
    warningMessage() {
      if (null === this.activeElement) {
        return '';
      }
      return this.getActiveWarnings(this.activeElement.uid)
        .map(warningKey => this.$t(`warning.${warningKey}`))
        .join('<br />');
    },
    getPrintStyle() {
      return this.$store.getters[GETTER_ACTIVE_PRINT_SIDE_PRINT_STYLE];
    }
  },
  mounted() {
    window.addEventListener('resize', this.computeCanvasSize);
    this.$store.subscribe(mutation => {
      if (mutation.type === MUTATION_SET_PRINT_SIDE) {
        this.closeModal('always', 'instant');
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.computeCanvasSize);
  },
  methods: {
    toggleGrid(val) {
      this.showGrid = val;
    },
    // Fetch clicks outside other components
    clickHandler() {
      this.$root.$emit('reset-navigation');
    },
    prev() {
      this.$refs.swiper.$refs.printSideSwiper.$emit('prev');
    },
    next() {
      this.$refs.swiper.$refs.printSideSwiper.$emit('next');
    },
    ...mapActions(useCompositionWarningStore, [
      'hideWarning',
      'hasActiveWarnings',
      'getActiveWarnings'
    ]),
    closeSizeWarning() {
      if (null === this.activeElement) {
        return;
      }
      this.hideWarning(this.activeElement.uid);
    },
    computeCanvasSize() {
      this.isMobile = isMobile();

      const printArea = this.$store.getters[GETTER_PRODUCT_PRINT_AREA](
        this.$store.getters[GETTER_ACTIVE_PRINT_SIDE]
      );

      this.clipPath = printArea.clipPath || DEFAULT_RECTANGLE_CLIP_PATH;

      if (printArea && this.$refs.ProductImage) {
        const printAreaMargin = 5;
        const maxWidth = this.$refs.ProductCanvas.clientWidth;

        const {
          naturalWidth: imageOriginalWidth,
          naturalHeight: imageOriginalHeight,
          width: imageWidth,
          height: imageHeight,
          offsetTop: productImageYCoordinate,
          offsetLeft: productImageXCoordinate
        } = this.$refs.ProductImage;

        this.scaleX = imageWidth / imageOriginalWidth;
        this.scaleY = imageHeight / imageOriginalHeight;
        this.width = printArea.width * this.scaleX;
        this.height = printArea.height * this.scaleY;
        this.top = printArea.y * this.scaleY + productImageYCoordinate;
        this.left = printArea.x * this.scaleX + productImageXCoordinate;
        this.ratio = printArea.width / printArea.height;

        const right = -(this.left + this.width - maxWidth);
        const delta = Math.min(this.left, right);

        if (delta < printAreaMargin) {
          const resizePixels = 2 * (Math.abs(delta) + printAreaMargin);

          this.$refs.ProductImage.style.width = `${
            imageWidth - resizePixels
          }px`;
          this.computeCanvasSize();
        }
      }
    },
    updatePrintplate(content, side) {
      this.$store.commit(MUTATION_COMPOSITION_UPDATE_SIDE_CONTENT, {
        side,
        content
      });
    },
    unselectActiveItem(event) {
      if (event.target === event.currentTarget) {
        this.unselectAllElements();
      }
    },
    ...mapActions(useControlLayerStore, ['unselectAllElements'])
  }
};
</script>
<style>
.toasted.toasted-primary.info.bg-sh-primary {
  background: #0082d5;
}
</style>
<style scoped>
.ProductImage {
  @apply max-w-none max-h-full block w-auto;
  height: fit-content;
}
.ProductCanvas {
  @apply relative w-auto flex;
}
.print-style-label {
  bottom: auto;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  mix-blend-mode: difference;
  color: white;
}
@screen xs {
  .ProductCanvas {
    height: 100%;
  }

  #visual-aid-menu {
    right: 2rem;
  }
}
@screen md {
  .ProductCanvas {
    padding-right: 100px;
  }
  .print-style-label {
    bottom: 20px;
    left: auto;
    right: 20px;
    transform: none;
    top: auto;
  }
}
@screen mdl {
  .ProductCanvas {
    padding-right: 150px;
  }
}
.navigator {
  @apply flex absolute justify-between w-1/2 border shadow-md rounded-lg;
  background-color: #fffa;
}
</style>
